<template>
  <section class="userList d-flex flex-column inherit-height" v-if="dataLoaded">
    <section class="pb-4 d-flex flex-column">
      <v-text-field
        rounded
        outlined
        hide-details
        prepend-inner-icon="mdi-magnify"
        class="mx-3 mt-2"
        :placeholder="$t('filter')"
        v-model="searchFilter"
        clearable
      ></v-text-field>

      <div class="filter-item">
        <span>{{ $t('bf.openRatingsOnly') }}</span>
        <v-switch v-model="showOnlyOpenEvaluations" hide-details />
      </div>

      <div class="filter-item" v-if="isMemberOfMysteryGroup">
        <!-- If user has subordinates -> show my-employees switch -->
        <span>{{ $t('bf.ownStaffOnly') }}</span>
        <v-switch v-model="showOnlyMyEmployees" hide-details />
      </div>
    </section>
    <v-divider></v-divider>

    <section class="scroll-container flex-grow-1 d-flex">
      <v-list two-line class="full-width">
        <v-list-item-group active-class="active-topic" class="mt-4 mx-3">
          <user-list-item
            @onSelected="onUserSelected(user)"
            v-for="user in sortedAndFilteredUsers"
            :key="user.id"
            :user="user"
            :open-evaluations="user.evaluations.open"
            :closed-evaluations="user.evaluations.closed"
          >
          </user-list-item>
        </v-list-item-group>
      </v-list>
    </section>
  </section>
</template>

<script>
import { mapState } from 'vuex';
import UserListItem from './UserListItem.vue';
import begleitfahrtUserService from '../services/begleitfahrtUserService';
import common from '@/mixins/common';
import sortFunctions from '@/sorters/sortFunctions';
import { CLOSED_EVALUATIONS_COUNT, CLOSED_RATINGS_COUNT, OPEN_EVALUATIONS_COUNT, OPEN_RATINGS_COUNT } from '@/common/constants';

export default {
  name: 'UserList',
  components: {
    UserListItem,
  },
  mixins: [common],
  props: {
    tileFilter: String,
  },
  data() {
    return {
      searchFilter: '',
      showOnlyOpenEvaluations: false,
      showOnlyMyEmployees: true,
      showOnlyClosedEvaluations: false,
    };
  },
  created() {
    const savedFilter = sessionStorage.getItem('searchFilter');
    if (savedFilter !== null) {
      this.searchFilter = savedFilter;
    } else {
      this.searchFilter = '';
    }
  },
  computed: {
    ...mapState('begleitfahrt', [
      'dataLoaded',
      'deviceUserBoss',
      'rating',
      'ratingsByUserId',
      'users',
      'questionnaireById',
      'questionnaireLocalData',
    ]),
    ...mapState('profile', ['user']),
    userId() {
      if (!this.user.userId) {
        throw new Error('UserId not available');
      }
      return this.user.userId;
    },
    evaluatedUserId() {
      return Number(this.$route.params.userId);
    },
    isMemberOfMysteryGroup() {
      return this.user.groups.includes('Begleitfahrt-Mystery');
    },
    subordinatesOfCurrentUser() {
      return this.usersEnhancedWithCounters.filter((user) => {
        return this.deviceUserBoss.some((bossUser) => bossUser.bossId === this.userId && bossUser.userId === user.id);
      });
    },
    usersEnhancedWithCounters() {
      return this.users
        .filter((u) => u.active && !u.deleted)
        .map((user) => {
          return {
            ...user,
            evaluations: {
              closed: begleitfahrtUserService.getCompletedRatingsCountForUser(user.id, this.ratingsByUserId, this.questionnaireById),
              open: begleitfahrtUserService.getOpenRatingsCountForUser(user.id, this.questionnaireLocalData),
              openRatingsCount: begleitfahrtUserService.getOpenRatingsCount(user.id, this.questionnaireLocalData, this.questionnaireById),
              closedRatingsCount: begleitfahrtUserService.getClosedRatingsCount(user.id, this.questionnaireById, this.ratingsByUserId),
              openEvaluationsCount: begleitfahrtUserService.getOpenEvaluationsCount(user.id, this.questionnaireLocalData, this.questionnaireById),
              closedEvaluationsCount: begleitfahrtUserService.getClosedEvaluationsCount(user.id, this.questionnaireById, this.ratingsByUserId),
            },
          };
        });
    },

    filteredUsers() {
      let filtered = [];

      this.$emit('showOnlyMyEmployeesEvent', this.showOnlyMyEmployees);

      if (this.showOnlyMyEmployees) {
        filtered = this.subordinatesOfCurrentUser;
      } else {
        filtered = this.usersEnhancedWithCounters;
      }

      if (this.tileFilter === OPEN_RATINGS_COUNT) {
        filtered = filtered.filter((item) => item.evaluations.openRatingsCount);
      } else if (this.tileFilter === CLOSED_RATINGS_COUNT) {
        filtered = filtered.filter((item) => item.evaluations.closedRatingsCount);
      } else if (this.tileFilter === OPEN_EVALUATIONS_COUNT) {
        filtered = filtered.filter((item) => item.evaluations.openEvaluationsCount);
      } else if (this.tileFilter === CLOSED_EVALUATIONS_COUNT) {
        filtered = filtered.filter((item) => item.evaluations.closedEvaluationsCount);
      }

      if (this.showOnlyOpenEvaluations) {
        filtered = filtered.filter((u) => u.evaluations.open > 0);
      }

      if (this.showOnlyClosedEvaluations) {
        filtered = filtered.filter((u) => u.evaluations.closed > 0);
      }

      if (this.searchFilter && this.searchFilter.length > 0) {
        const currentSearchFilters = this.searchFilter
          .toLowerCase()
          .split(' ')
          .map((text) => text.trim());

        filtered = filtered.filter((user) => {
          if (!user.firstname && !user.lastname) return false;
          let concat = `${user.firstname ? user.firstname : ''} ${user.lastname ? user.lastname : ''}`.toLowerCase();

          return currentSearchFilters.every((searchText) => concat.includes(searchText));
        });
      } else {
        filtered = filtered.filter((user) => user.firstname || user.lastname);
      }

      return filtered;
    },

    sortedAndFilteredUsers() {
      return this.filteredUsers.slice().sort(sortFunctions.compareLastNameAndFirstName);
    },
  },

  methods: {
    onUserSelected(user) {
      this.mx_isLeftDrawerOpen = false;
      this.$router.push(`/begleitfahrt/${this.mx_instance}/${user.id}`);
    },
  },
  watch: {
    searchFilter(newValue, oldValue) {
      if (newValue === null) {
        newValue = '';
      }
      sessionStorage.setItem('searchFilter', newValue);
    },
  },
};
</script>
<style scoped>
.filter-item {
  display: flex;
  height: 50px;
  margin: 0 12px 0 28px;
  align-items: flex-end;
  justify-content: space-between;
}
</style>
